// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import catalogStyles from '../../bank-upload/styles/institution-catalog.module.css';
import formStyles from '../../bank-upload/styles/bank-upload-form.module.css';
import { LinkedLiquiditySection } from '@client/banks/components/shared/linked-liquidity-section';
import { DateInput } from '@shared/ui-kit/components/date-input';
import { DeleteButton } from '@shared/ui-kit/components/delete-button';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { Input, Label } from '@shared/ui-kit/components/input';
import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Divider, Tooltip, Typography } from '@mui/material';
import { Fragment } from 'react';
import { Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useDebtForm } from './use-debt-form';

export const DebtForm = defineComponent({
  displayName: 'DebtForm',
  useSetup: useDebtForm,
  View: ({
    formId,
    onSubmit,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange,
    control,
    handleSubmit,
    t,
    currencyPrefix,
    amortizationsFieldArray,
    productTypeOptions,
    amortizationStatusOptions,
    todayMaxDate,
    isLoan,
    isCreditLine,
    onAddAmortization,
  }) => (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
        <LinkedLiquiditySection
          currencyOptions={currencyOptions}
          accountIbanOptions={accountIbanOptions}
          defaultCurrency={defaultCurrency}
          apiRef={linkedLiquidityApiRef}
          onGateChange={onLinkedLiquidityGateChange}
          labelI18nKey="aggregator.create-bank.debts.account"
        />

        <section className={formStyles['form-section']}>
          <Typography
            component="h3"
            variant="subtitle1"
            className={formStyles['section-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.debts.section.product"
              ns="aggregator"
            />
          </Typography>

          <div className={formStyles['field-grid']}>
            <Controller
              control={control}
              name="productType"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label required error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.debts.product-type"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...productTypeOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t('aggregator.create-bank.debts.product-type', {
                      ns: 'aggregator',
                    })}
                    selectType="white"
                    data-testid="bank-upload-debt-product-type"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-debt-name"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.debts.name"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="bank-upload-debt-name"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.debts.name-placeholder',
                      {
                        ns: 'aggregator',
                      },
                    )}
                    data-testid="bank-upload-debt-name"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="currency"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label required error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.currency"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...currencyOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t('aggregator.create-bank.accounts.currency', {
                      ns: 'aggregator',
                    })}
                    selectType="white"
                    data-testid="bank-upload-debt-currency"
                  />
                </div>
              )}
            />

            {isLoan ? (
              <Controller
                control={control}
                name="pendingAmount"
                render={({ field, fieldState }) => (
                  <div className={catalogStyles['field-stack']}>
                    <Label
                      htmlFor="bank-upload-debt-pending-amount"
                      required
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="aggregator.loans.pending-amount"
                        ns="aggregator"
                      />
                    </Label>
                    <NumericFormat
                      customInput={Input}
                      id="bank-upload-debt-pending-amount"
                      value={field.value || ''}
                      onValueChange={({ floatValue }) =>
                        field.onChange(floatValue ?? 0)
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      inputMode="decimal"
                      allowNegative={false}
                      decimalScale={2}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                      error={!!fieldState.error}
                      placeholder={t(
                        'aggregator.create-bank.debts.pending-amount-placeholder',
                        { ns: 'aggregator' },
                      )}
                      data-testid="bank-upload-debt-pending-amount"
                    />
                  </div>
                )}
              />
            ) : null}

            {isCreditLine ? (
              <>
                <Controller
                  control={control}
                  name="limitAmount"
                  render={({ field, fieldState }) => (
                    <div className={catalogStyles['field-stack']}>
                      <Label
                        htmlFor="bank-upload-debt-credit-limit"
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="aggregator.loans.credit-limit"
                          ns="aggregator"
                        />
                      </Label>
                      <NumericFormat
                        customInput={Input}
                        id="bank-upload-debt-credit-limit"
                        value={field.value ?? ''}
                        onValueChange={({ floatValue }) =>
                          field.onChange(floatValue ?? undefined)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputMode="decimal"
                        allowNegative={false}
                        decimalScale={2}
                        thousandSeparator="."
                        decimalSeparator=","
                        allowedDecimalSeparators={DECIMAL_SEPARATORS}
                        prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                        error={!!fieldState.error}
                        placeholder={t(
                          'aggregator.create-bank.debts.credit-limit-placeholder',
                          { ns: 'aggregator' },
                        )}
                        data-testid="bank-upload-debt-credit-limit"
                      />
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name="disposed"
                  render={({ field, fieldState }) => (
                    <div className={catalogStyles['field-stack']}>
                      <Label
                        htmlFor="bank-upload-debt-disposed"
                        required
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="aggregator.loans.utilized-balance"
                          ns="aggregator"
                        />
                      </Label>
                      <NumericFormat
                        customInput={Input}
                        id="bank-upload-debt-disposed"
                        value={field.value ?? ''}
                        onValueChange={({ floatValue }) =>
                          field.onChange(floatValue ?? undefined)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputMode="decimal"
                        allowNegative={false}
                        decimalScale={2}
                        thousandSeparator="."
                        decimalSeparator=","
                        allowedDecimalSeparators={DECIMAL_SEPARATORS}
                        prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                        error={!!fieldState.error}
                        placeholder={t(
                          'aggregator.create-bank.debts.disposed-placeholder',
                          { ns: 'aggregator' },
                        )}
                        data-testid="bank-upload-debt-disposed"
                      />
                    </div>
                  )}
                />
              </>
            ) : null}

            <Controller
              control={control}
              name="snapshotDate"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-debt-snapshot-date"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.debts.snapshot-date"
                      ns="aggregator"
                    />
                  </Label>
                  <DateInput
                    id="bank-upload-debt-snapshot-date"
                    value={field.value}
                    onChange={field.onChange}
                    maxDate={todayMaxDate}
                    error={!!fieldState.error}
                    data-testid="bank-upload-debt-snapshot-date"
                  />
                </div>
              )}
            />
          </div>
        </section>

        <section className={formStyles['form-section']}>
          <Typography
            component="h3"
            variant="subtitle1"
            className={formStyles['section-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.debts.section.contract"
              ns="aggregator"
            />
          </Typography>

          <div className={formStyles['field-grid']}>
            <Controller
              control={control}
              name="contractNumber"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-debt-contract-number"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.debts.contract-number"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="bank-upload-debt-contract-number"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.debts.contract-number-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-debt-contract-number"
                  />
                </div>
              )}
            />

            {isLoan ? (
              <Controller
                control={control}
                name="grantedCapital"
                render={({ field, fieldState }) => (
                  <div className={catalogStyles['field-stack']}>
                    <Label
                      htmlFor="bank-upload-debt-granted-capital"
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="aggregator.loans.granted-capital"
                        ns="aggregator"
                      />
                    </Label>
                    <NumericFormat
                      customInput={Input}
                      id="bank-upload-debt-granted-capital"
                      value={field.value ?? ''}
                      onValueChange={({ floatValue }) =>
                        field.onChange(floatValue ?? undefined)
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      inputMode="decimal"
                      allowNegative={false}
                      decimalScale={2}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                      error={!!fieldState.error}
                      placeholder={t(
                        'aggregator.create-bank.debts.granted-capital-placeholder',
                        { ns: 'aggregator' },
                      )}
                      data-testid="bank-upload-debt-granted-capital"
                    />
                  </div>
                )}
              />
            ) : null}

            <Controller
              control={control}
              name="interestRate"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-debt-interest-rate"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.loans.interest-rate"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-debt-interest-rate"
                    value={field.value ?? ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? undefined)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative={false}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.debts.interest-rate-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-debt-interest-rate"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="constitutionDate"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-debt-constitution-date"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.debts.constitution-date"
                      ns="aggregator"
                    />
                  </Label>
                  <DateInput
                    id="bank-upload-debt-constitution-date"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    maxDate={todayMaxDate}
                    error={!!fieldState.error}
                    data-testid="bank-upload-debt-constitution-date"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="expirationDate"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-debt-expiration-date"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.debts.expiration-date"
                      ns="aggregator"
                    />
                  </Label>
                  <DateInput
                    id="bank-upload-debt-expiration-date"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    data-testid="bank-upload-debt-expiration-date"
                  />
                </div>
              )}
            />

            {isLoan ? (
              <>
                <Controller
                  control={control}
                  name="nextInstallment"
                  render={({ field, fieldState }) => (
                    <div className={catalogStyles['field-stack']}>
                      <Label
                        htmlFor="bank-upload-debt-next-installment"
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="aggregator.loans.monthly-payment"
                          ns="aggregator"
                        />
                      </Label>
                      <NumericFormat
                        customInput={Input}
                        id="bank-upload-debt-next-installment"
                        value={field.value ?? ''}
                        onValueChange={({ floatValue }) =>
                          field.onChange(floatValue ?? undefined)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputMode="decimal"
                        allowNegative={false}
                        decimalScale={2}
                        thousandSeparator="."
                        decimalSeparator=","
                        allowedDecimalSeparators={DECIMAL_SEPARATORS}
                        prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                        error={!!fieldState.error}
                        placeholder={t(
                          'aggregator.create-bank.debts.next-installment-placeholder',
                          { ns: 'aggregator' },
                        )}
                        data-testid="bank-upload-debt-next-installment"
                      />
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name="pendingReceipts"
                  render={({ field, fieldState }) => (
                    <div className={catalogStyles['field-stack']}>
                      <Label
                        htmlFor="bank-upload-debt-pending-receipts"
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="aggregator.create-bank.debts.pending-receipts"
                          ns="aggregator"
                        />
                      </Label>
                      <NumericFormat
                        customInput={Input}
                        id="bank-upload-debt-pending-receipts"
                        value={field.value ?? ''}
                        onValueChange={({ floatValue }) =>
                          field.onChange(floatValue ?? undefined)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputMode="numeric"
                        allowNegative={false}
                        decimalScale={0}
                        thousandSeparator="."
                        decimalSeparator=","
                        allowedDecimalSeparators={DECIMAL_SEPARATORS}
                        error={!!fieldState.error}
                        placeholder={t(
                          'aggregator.create-bank.debts.pending-receipts-placeholder',
                          { ns: 'aggregator' },
                        )}
                        data-testid="bank-upload-debt-pending-receipts"
                      />
                    </div>
                  )}
                />
              </>
            ) : null}
          </div>
        </section>

        {isLoan ? (
          <section className={formStyles['form-section']}>
            <div className={formStyles['section-title-row']}>
              <Typography
                component="h3"
                variant="subtitle1"
                className={formStyles['section-title']}
              >
                <Translation
                  i18nKey="aggregator.create-bank.debts.section.amortizations"
                  ns="aggregator"
                />
              </Typography>
              <Tooltip
                title={
                  <Translation
                    i18nKey="aggregator.create-bank.debts.add-amortization"
                    ns="aggregator"
                  />
                }
                placement="top"
              >
                <span className={formStyles['section-add-button']}>
                  <IconButton
                    type="button"
                    icon="Plus"
                    variant="icon"
                    color="gray"
                    onClick={onAddAmortization}
                    data-testid="bank-upload-debt-add-amortization"
                  />
                </span>
              </Tooltip>
            </div>

            {amortizationsFieldArray.fields.length > 0 ? (
              <div className={formStyles['repeatable-list-divided']}>
                {amortizationsFieldArray.fields.map((field, index) => (
                  <Fragment key={field.id}>
                    {index > 0 ? (
                      <Divider className={formStyles['repeatable-divider']} />
                    ) : null}

                    <fieldset
                      className={formStyles['repeatable-block']}
                      data-testid={`bank-upload-debt-amortization-${index}`}
                    >
                      <div className={formStyles['repeatable-item-header-row']}>
                        <Label
                          component="legend"
                          className={formStyles['repeatable-item-title']}
                        >
                          <Translation
                            i18nKey="aggregator.create-bank.debts.amortization-row-label"
                            ns="aggregator"
                            values={{ index: index + 1 }}
                          />
                        </Label>

                        <span className={formStyles['section-add-button']}>
                          <DeleteButton
                            onClick={() => amortizationsFieldArray.remove(index)}
                            data-testid={`bank-upload-debt-amortization-remove-${index}`}
                            color="gray"
                          />
                        </span>
                      </div>

                      <div className={formStyles['field-grid']}>
                        <Controller
                          control={control}
                          name={`amortizations.${index}.installmentDate`}
                          render={({ field: dateField, fieldState }) => (
                            <div className={catalogStyles['field-stack']}>
                              <Label
                                htmlFor={`bank-upload-debt-installment-date-${index}`}
                                required
                                error={!!fieldState.error}
                              >
                                <Translation
                                  i18nKey="aggregator.create-bank.debts.installment-date"
                                  ns="aggregator"
                                />
                              </Label>
                              <DateInput
                                id={`bank-upload-debt-installment-date-${index}`}
                                value={dateField.value}
                                onChange={dateField.onChange}
                                error={!!fieldState.error}
                                data-testid={`bank-upload-debt-installment-date-${index}`}
                              />
                            </div>
                          )}
                        />

                        <Controller
                          control={control}
                          name={`amortizations.${index}.status`}
                          render={({ field: statusField, fieldState }) => (
                            <div className={catalogStyles['field-stack']}>
                              <Label error={!!fieldState.error}>
                                <Translation
                                  i18nKey="aggregator.create-bank.debts.amortization-status"
                                  ns="aggregator"
                                />
                              </Label>
                              <OptionsSelector
                                optionsAvailable={[...amortizationStatusOptions]}
                                optionSelected={statusField.value ?? ''}
                                onChangeValueSelected={statusField.onChange}
                                defaultText={t(
                                  'aggregator.create-bank.debts.amortization-status',
                                  { ns: 'aggregator' },
                                )}
                                selectType="white"
                                data-testid={`bank-upload-debt-amortization-status-${index}`}
                              />
                            </div>
                          )}
                        />

                        <Controller
                          control={control}
                          name={`amortizations.${index}.amortized`}
                          render={({ field: amountField, fieldState }) => (
                            <div className={catalogStyles['field-stack']}>
                              <Label
                                htmlFor={`bank-upload-debt-amortized-${index}`}
                                error={!!fieldState.error}
                              >
                                <Translation
                                  i18nKey="aggregator.create-bank.debts.amortized"
                                  ns="aggregator"
                                />
                              </Label>
                              <NumericFormat
                                customInput={Input}
                                id={`bank-upload-debt-amortized-${index}`}
                                value={amountField.value ?? ''}
                                onValueChange={({ floatValue }) =>
                                  amountField.onChange(floatValue ?? undefined)
                                }
                                onBlur={amountField.onBlur}
                                name={amountField.name}
                                inputMode="decimal"
                                allowNegative={false}
                                decimalScale={2}
                                thousandSeparator="."
                                decimalSeparator=","
                                allowedDecimalSeparators={DECIMAL_SEPARATORS}
                                prefix={
                                  currencyPrefix ? `${currencyPrefix} ` : undefined
                                }
                                error={!!fieldState.error}
                                placeholder={t(
                                  'aggregator.create-bank.debts.amortized-placeholder',
                                  { ns: 'aggregator' },
                                )}
                                data-testid={`bank-upload-debt-amortized-${index}`}
                              />
                            </div>
                          )}
                        />

                        <Controller
                          control={control}
                          name={`amortizations.${index}.interest`}
                          render={({ field: amountField, fieldState }) => (
                            <div className={catalogStyles['field-stack']}>
                              <Label
                                htmlFor={`bank-upload-debt-interest-amount-${index}`}
                                error={!!fieldState.error}
                              >
                                <Translation
                                  i18nKey="aggregator.create-bank.debts.interest-amount"
                                  ns="aggregator"
                                />
                              </Label>
                              <NumericFormat
                                customInput={Input}
                                id={`bank-upload-debt-interest-amount-${index}`}
                                value={amountField.value ?? ''}
                                onValueChange={({ floatValue }) =>
                                  amountField.onChange(floatValue ?? undefined)
                                }
                                onBlur={amountField.onBlur}
                                name={amountField.name}
                                inputMode="decimal"
                                allowNegative={false}
                                decimalScale={2}
                                thousandSeparator="."
                                decimalSeparator=","
                                allowedDecimalSeparators={DECIMAL_SEPARATORS}
                                prefix={
                                  currencyPrefix ? `${currencyPrefix} ` : undefined
                                }
                                error={!!fieldState.error}
                                placeholder={t(
                                  'aggregator.create-bank.debts.interest-amount-placeholder',
                                  { ns: 'aggregator' },
                                )}
                                data-testid={`bank-upload-debt-interest-amount-${index}`}
                              />
                            </div>
                          )}
                        />

                        <Controller
                          control={control}
                          name={`amortizations.${index}.total`}
                          render={({ field: amountField, fieldState }) => (
                            <div className={catalogStyles['field-stack']}>
                              <Label
                                htmlFor={`bank-upload-debt-total-installment-${index}`}
                                error={!!fieldState.error}
                              >
                                <Translation
                                  i18nKey="aggregator.create-bank.debts.total-installment"
                                  ns="aggregator"
                                />
                              </Label>
                              <NumericFormat
                                customInput={Input}
                                id={`bank-upload-debt-total-installment-${index}`}
                                value={amountField.value ?? ''}
                                onValueChange={({ floatValue }) =>
                                  amountField.onChange(floatValue ?? undefined)
                                }
                                onBlur={amountField.onBlur}
                                name={amountField.name}
                                inputMode="decimal"
                                allowNegative={false}
                                decimalScale={2}
                                thousandSeparator="."
                                decimalSeparator=","
                                allowedDecimalSeparators={DECIMAL_SEPARATORS}
                                prefix={
                                  currencyPrefix ? `${currencyPrefix} ` : undefined
                                }
                                error={!!fieldState.error}
                                placeholder={t(
                                  'aggregator.create-bank.debts.total-installment-placeholder',
                                  { ns: 'aggregator' },
                                )}
                                data-testid={`bank-upload-debt-total-installment-${index}`}
                              />
                            </div>
                          )}
                        />

                        <Controller
                          control={control}
                          name={`amortizations.${index}.pendingCapital`}
                          render={({ field: amountField, fieldState }) => (
                            <div className={catalogStyles['field-stack']}>
                              <Label
                                htmlFor={`bank-upload-debt-pending-capital-${index}`}
                                error={!!fieldState.error}
                              >
                                <Translation
                                  i18nKey="aggregator.create-bank.debts.pending-capital"
                                  ns="aggregator"
                                />
                              </Label>
                              <NumericFormat
                                customInput={Input}
                                id={`bank-upload-debt-pending-capital-${index}`}
                                value={amountField.value ?? ''}
                                onValueChange={({ floatValue }) =>
                                  amountField.onChange(floatValue ?? undefined)
                                }
                                onBlur={amountField.onBlur}
                                name={amountField.name}
                                inputMode="decimal"
                                allowNegative={false}
                                decimalScale={2}
                                thousandSeparator="."
                                decimalSeparator=","
                                allowedDecimalSeparators={DECIMAL_SEPARATORS}
                                prefix={
                                  currencyPrefix ? `${currencyPrefix} ` : undefined
                                }
                                error={!!fieldState.error}
                                placeholder={t(
                                  'aggregator.create-bank.debts.pending-capital-placeholder',
                                  { ns: 'aggregator' },
                                )}
                                data-testid={`bank-upload-debt-pending-capital-${index}`}
                              />
                            </div>
                          )}
                        />
                      </div>
                    </fieldset>
                  </Fragment>
                ))}
              </div>
            ) : (
              <div
                className={formStyles['section-empty-hint']}
                data-testid="bank-upload-debt-amortizations-empty"
              >
                <Typography
                  variant="body2"
                  className={formStyles['section-empty-message']}
                >
                  <Translation
                    i18nKey="aggregator.create-bank.debts.amortizations-empty-hint"
                    ns="aggregator"
                  />
                </Typography>
              </div>
            )}
          </section>
        ) : null}

        {isCreditLine ? (
          <section className={formStyles['form-section']}>
            <Typography
              component="h3"
              variant="subtitle1"
              className={formStyles['section-title']}
            >
              <Translation
                i18nKey="aggregator.create-bank.debts.section.credit-line"
                ns="aggregator"
              />
            </Typography>

            <div className={formStyles['field-grid']}>
              <Controller
                control={control}
                name="available"
                render={({ field, fieldState }) => (
                  <div className={catalogStyles['field-stack']}>
                    <Label
                      htmlFor="bank-upload-debt-available"
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="aggregator.loans.available-balance"
                        ns="aggregator"
                      />
                    </Label>
                    <NumericFormat
                      customInput={Input}
                      id="bank-upload-debt-available"
                      value={field.value ?? ''}
                      onValueChange={({ floatValue }) =>
                        field.onChange(floatValue ?? undefined)
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      inputMode="decimal"
                      allowNegative={false}
                      decimalScale={2}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                      error={!!fieldState.error}
                      placeholder={t(
                        'aggregator.create-bank.debts.available-placeholder',
                        { ns: 'aggregator' },
                      )}
                      data-testid="bank-upload-debt-available"
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="currentBalance"
                render={({ field, fieldState }) => (
                  <div className={catalogStyles['field-stack']}>
                    <Label
                      htmlFor="bank-upload-debt-current-balance"
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="aggregator.create-bank.debts.current-balance"
                        ns="aggregator"
                      />
                    </Label>
                    <NumericFormat
                      customInput={Input}
                      id="bank-upload-debt-current-balance"
                      value={field.value ?? ''}
                      onValueChange={({ floatValue }) =>
                        field.onChange(floatValue ?? undefined)
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      inputMode="decimal"
                      allowNegative={false}
                      decimalScale={2}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                      error={!!fieldState.error}
                      placeholder={t(
                        'aggregator.create-bank.debts.current-balance-placeholder',
                        { ns: 'aggregator' },
                      )}
                      data-testid="bank-upload-debt-current-balance"
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="nextPaymentDate"
                render={({ field, fieldState }) => (
                  <div className={catalogStyles['field-stack']}>
                    <Label
                      htmlFor="bank-upload-debt-next-payment-date"
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="aggregator.create-bank.debts.next-payment-date"
                        ns="aggregator"
                      />
                    </Label>
                    <DateInput
                      id="bank-upload-debt-next-payment-date"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                      data-testid="bank-upload-debt-next-payment-date"
                    />
                  </div>
                )}
              />
            </div>
          </section>
        ) : null}
      </fieldset>
    </form>
  ),
});
